import { DownloadIcon } from '@heroicons/react/outline';
import React, { useState } from 'react';
import { useActions, useAppState } from '@frontend/howl-web-app/overmind';
import { RadioGroup } from '@headlessui/react';
import Button from '../../components/ui/button';
import { LoadingSpinner } from '../ui/components/LoadingSpinner';
import { cloneDeep } from 'lodash';
import { SnackBar } from '../../components/ui/snackbar/SnackBar';
import { useClickOutside } from '@mantine/hooks';
import classNames from 'classnames';
export interface ReportDropdownType {
  rowCount?: number | null; //Total number of rows in a table/list.
  visibleRows?: number;
  params: {
    page?: number;
    perPage?: number;
    dateTo?: string;
    dateFrom?: string;
    reportParams?: any;
    additionalSlug?: string;
    giftingAvailable?: boolean;
    onSale?: boolean;
    productLaunch?: boolean;
    flatFee?: boolean;
    cpc?: boolean;
    direction?: string;
    orderBy?: string;
  };
  circleButton?: boolean;
}

export const ReportDropdown = (props: ReportDropdownType) => {
  const { isPolling, error } = useAppState().report;
  const { requestReport, clearError } = useActions().report;
  const { checkAllSites } = useActions().publisherDashboard;
  const [toggleDropdown, setToggleDropdown] = useState<boolean>(false);
  const [download, setDownload] = useState('visible');
  const ref = useClickOutside(() => setToggleDropdown(false));

  const downloadReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    setToggleDropdown(false);
    const requestPayload: {
      page?: number;
      perPage?: number;
      dateTo?: string;
      dateFrom?: string;
      reportParams?: any;
      additionalSlug?: string;
      giftingAvailable?: boolean;
      onSale?: boolean;
      productLaunch?: boolean;
      flatFee?: boolean;
      cpc?: boolean;
      direction?: string;
      orderBy?: string;
    } = cloneDeep({
      rowCount: props.rowCount,
      dateTo: props.params.dateTo,
      dateFrom: props.params.dateFrom,
      giftingAvailable: props.params.giftingAvailable,
      onSale: props.params.onSale,
      productLaunch: props.params.productLaunch,
      flatFee: props.params.flatFee,
      cpc: props.params.cpc,
      direction: props.params.direction,
      orderBy: props.params.orderBy,
    });
    if (download === 'visible') {
      requestPayload.page = props.params.page;
      requestPayload.perPage = props.params.perPage;
    } else if (download === 'all') {
      requestPayload.page = 1;
      requestPayload.perPage = props.rowCount as any;
    }

    if (props.params.reportParams) {
      requestPayload.reportParams = props.params.reportParams;
    }

    if (props.params.additionalSlug) {
      requestPayload.additionalSlug = props.params.additionalSlug;
    }

    // check for All Sites additional orgs (updates requestPayload w/additional_orgs)
    checkAllSites({ params: requestPayload, isReport: true });
    requestReport(requestPayload);
  };
  if (isPolling) {
    return (
      <div className="flex items-center">
        <LoadingSpinner containerClassName="w-6 h-6" />
      </div>
    );
  } else {
    return (
      <div
        className={classNames('relative flex items-center', {
          'w-full': props.circleButton,
        })}
      >
        <button
          className={classNames(
            'bg-none m-0 p-0 cursor-pointer w-full h-full hover:bg-hover-secondary focus:bg-focus-secondary border border-deep-space-200',
            {
              'rounded-lg px-3': !props.circleButton,
              'rounded-full': props.circleButton,
            },
          )}
          onClick={() => setToggleDropdown(!toggleDropdown)}
        >
          <DownloadIcon
            className={classNames('w-5 h-5 text-black', {
              'mx-auto': props.circleButton,
            })}
          />
        </button>

        {toggleDropdown ? (
          <div
            className="absolute top-12 right-0 z-20 shadow-lg p-4 bg-white"
            ref={ref}
          >
            <RadioGroup value={download} onChange={setDownload}>
              <RadioGroup.Label>
                <span className="text-bold text-sm mb-4 block">Export CSV</span>
              </RadioGroup.Label>
              <RadioGroup.Option value="visible">
                {({ checked }) => (
                  <span className="text-sm cursor-pointer block p-2 rounded-md hover:bg-gray-200">
                    <div className="rounded-full w-4 h-4 border border-black float-left flex items-center justify-center mr-2">
                      {checked ? (
                        <div className="rounded-full w-3 h-3 bg-black"></div>
                      ) : null}
                    </div>
                    Visible ({props.visibleRows})
                  </span>
                )}
              </RadioGroup.Option>
              {props.rowCount ? (
                <RadioGroup.Option value="all">
                  {({ checked }) => (
                    <span className="text-sm cursor-pointer block p-2 rounded-md hover:bg-gray-200">
                      <div className="rounded-full w-4 h-4 border border-black float-left flex items-center justify-center mr-2">
                        {checked ? (
                          <div className="rounded-full w-3 h-3 bg-black"></div>
                        ) : null}
                      </div>
                      All Rows ({props.rowCount})
                    </span>
                  )}
                </RadioGroup.Option>
              ) : null}
            </RadioGroup>

            <div className="w-full flex justify-between mt-4">
              <Button
                kind="text"
                onClick={() => {
                  setToggleDropdown(false);
                }}
                label="Cancel"
              />

              <Button
                kind="text"
                label="Download"
                onClick={(e: any) => {
                  downloadReport(e);
                }}
              />
            </div>
          </div>
        ) : null}

        {error.showError ? (
          <SnackBar
            message={error.errorMessage}
            type="error"
            setSnackbarTxt={() => clearError()}
            timeout={3}
          />
        ) : null}
      </div>
    );
  }
};
